import { useState } from 'react'
import type { CotMePerson } from '../../types/community'
import { Logo } from '../shell/Logo'
import { cn } from '../../lib/cn'

// Profile-switch UI (COTC_GroupsModelRevision R12/R13): the guardian-PIN prompt shown when switching
// up to an adult profile, and the R13 chooser shown when a family with managed profiles has no
// acting session yet. Both are driven entirely by PersonProvider.

function personLabel(p: CotMePerson): string {
  if (p.isAccountHolder) return `${p.firstName} · adult (you)`
  if (p.kind === 'Guardian') return `${p.firstName} · adult`
  if (p.kind === 'ManagedAdult') return `${p.firstName} · adult, managed`
  return p.ageYears != null ? `${p.firstName} · ${p.ageYears}` : p.firstName
}

/** Full-screen "who's using this?" chooser — R13 least-privilege default. */
export function ProfileChooser({
  people,
  onPick,
  busyId,
}: {
  people: CotMePerson[]
  onPick: (personId: string) => void
  busyId: string | null
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-page px-6 py-12">
      <div className="w-full max-w-[480px] animate-rise rounded-card border border-line-soft bg-paper-surface p-8 shadow-card">
        <div className="mb-6">
          <Logo insetColor="#fffdf8" />
        </div>
        <h1 className="font-serif text-[24px] font-medium tracking-[-.015em] text-ink">Who&rsquo;s using this?</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
          Pick a profile to continue. Switching to an adult needs the family PIN.
        </p>
        <div className="mt-5 grid gap-2">
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busyId === p.id}
              onClick={() => onPick(p.id)}
              className={cn(
                'flex items-center gap-3 rounded-input border border-line-soft bg-transparent px-4 py-3 text-left transition-colors hover:border-terra',
                busyId === p.id && 'opacity-60',
              )}
            >
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-paper-panel font-mono text-[13px] text-ink">
                {(p.firstName[0] ?? '?').toUpperCase()}
              </span>
              <span className="text-[14px] text-ink">{personLabel(p)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Guardian-PIN prompt shown when switching up to an adult profile (R12). */
export function PinPromptModal({
  person,
  error,
  busy,
  onSubmit,
  onCancel,
}: {
  person: CotMePerson
  error: string | null
  busy: boolean
  onSubmit: (pin: string) => void
  onCancel: () => void
}) {
  const [pin, setPin] = useState('')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(36,31,26,.42)] px-6">
      <div role="dialog" aria-modal="true" aria-labelledby="guardian-pin-title" onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) onCancel()
        if (event.key !== 'Tab') return
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('input, button:not(:disabled)'))
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }} className="w-full max-w-[380px] animate-rise-modal rounded-card border border-line-soft bg-paper-surface p-7 shadow-modal">
        <h2 id="guardian-pin-title" className="font-serif text-[20px] font-medium text-ink">Enter the guardian PIN</h2>
        <p className="mt-2 text-[12.5px] leading-relaxed text-ink-muted">
          Switching to {person.firstName}&rsquo;s adult profile needs the family PIN.
        </p>
        <form
          className="mt-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!busy && pin) onSubmit(pin)
          }}
        >
          <input
            autoFocus
            type="password"
            aria-label="Guardian PIN"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="••••"
            className="w-full rounded-input border border-line bg-paper-surface px-4 py-3 text-center font-mono text-[20px] tracking-[.3em] text-ink outline-none focus:border-terra"
          />
          {error && (
            <div role="alert" className="mt-3 rounded-input bg-honey-bg px-3 py-2 text-[12px] text-honey-fg">
              {error}
            </div>
          )}
          <div className="mt-5 flex items-center justify-end gap-3">
            <button type="button" onClick={onCancel} className="rounded-btn border border-line px-[16px] py-[10px] text-[13px] font-medium text-ink-muted hover:border-terra hover:text-terra-deep">
              Cancel
            </button>
            <button type="submit" disabled={busy || pin.length < 4} className="rounded-btn bg-ink px-[16px] py-[10px] text-[13px] font-medium text-paper-panel disabled:opacity-60">
              {busy ? 'Checking…' : 'Switch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
